import { HpDateValidator, HpValidator } from '@avantia/form-validation';
import { createControlId } from '@avantia/questionset-model';
import { SvgIcon } from '@avantia/style-guide';
import $ from 'jquery';
import React, { BaseSyntheticEvent } from 'react';
import { FormControl } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { HpDatePart, formatDate, isEarlierDay, isSameDay } from '../../../tools/dateTools';
import { HpClientQuestion, HpClientStandardInputTextAndStyle, HpViewportState } from '../../../types';
import { HpInputControlProps, HpOnFocusEvent, HpOnMouseEvent } from '../../../types/inputControlTypes';
import { ImageWidget } from '../../widgets';
import { StandardFormGroupWidget } from '../../widgets/standardFormGroupWidget';
import { isDisabled } from '../common/tools';
import { getMinMaxDateValuesFromValidator } from '../controlUtilities';

const calendarSrc = 'datePickerCalendars/date-picker-icon.png';

export interface HpDatePickerControlProps extends HpInputControlProps {
  placeholder?: string;
}

export function DatePickerControl(props: HpDatePickerControlProps): JSX.Element {
  return DatePickerControlCommon({ ...props, controlVariant: 'multiple-inputs' });
}

export function DatePickerSingleInputControl(props: HpDatePickerControlProps): JSX.Element {
  return DatePickerControlCommon({ ...props, controlVariant: 'single-input' });
}

type HpControlVariant = 'single-input' | 'multiple-inputs';

interface HpDatePickerCommonControlProps extends HpDatePickerControlProps {
  controlVariant: HpControlVariant;
}

function DatePickerControlCommon({
  answerSource,
  controlVariant,
  data,
  dataType,
  errors,
  formatType,
  id,
  name,
  onChange,
  question,
  questionText,
  subtext,
  type,
  ui,
  value: answerValue,
  viewport
}: HpDatePickerCommonControlProps): JSX.Element {
  const error = errors ? errors[id] : undefined;
  const { textAndStyle: dataTextAndStyle, validation } = data;
  const textAndStyle = dataTextAndStyle[formatType] as HpClientStandardInputTextAndStyle;
  const { placeholder } = textAndStyle;
  const value = answerValue ? new Date(answerValue as string) : undefined;
  const { minValue, maxValue, monthsShown, showSingleMonth, showPreviousMonths, focusSelectedMonth } =
    getDatePickerOptions({ validation, viewport });

  let date: Date;

  const dateChangeHandler = createCustomHandler({
    action: 'slideUp',
    question,
    handler: onChange,
    preludeAction: () => formatDate(date, 'YYYY-MM-DD')
  });

  // The 'ref' argument is not used, but will throw a console error if we omit it.
  const DateInput = (
    htmlProps: React.HTMLProps<HTMLInputElement | HTMLButtonElement>,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    ref: React.Ref<HTMLInputElement>
  ): JSX.Element => {
    const { onClick } = htmlProps;
    const disabled = isDisabled(question, ui);

    const props: HpCreateDateInputsProps = {
      questionText,
      name,
      onClick,
      disabled,
      placeholder,
      questionId: id,
      type,
      value
    };

    let dateInput: JSX.Element;
    let calendarIcon: JSX.Element | null = (
      <CalendarIcon controlVariant={controlVariant} onClick={onClick} question={question} />
    );

    switch (controlVariant) {
      case 'single-input':
        dateInput = <DateInputSingle {...props} calendarIcon={calendarIcon} disabled={disabled} />;
        calendarIcon = null;
        break;
      case 'multiple-inputs':
        dateInput = <DateInputs {...props} />;
        break;
    }

    if (disabled) {
      calendarIcon = null;
    }

    return (
      <div className="date-input-group" role="group">
        {dateInput}
        {calendarIcon}
      </div>
    );
  };

  const calendar = (
    <DatePicker
      key={id}
      id={id}
      onChange={(d: Date, e: React.SyntheticEvent<BaseSyntheticEvent, any>): void => {
        date = d;
        dateChangeHandler(e as BaseSyntheticEvent);
      }}
      dateFormat="dd/MM/yyyy"
      selected={value}
      monthsShown={monthsShown}
      showYearDropdown={monthsShown > 3}
      withPortal={showSingleMonth}
      showPreviousMonths={showPreviousMonths}
      focusSelectedMonth={focusSelectedMonth}
      minDate={minValue}
      maxDate={maxValue}
      showDisabledMonthNavigation={false}
      disabledKeyboardNavigation={false}
      dayClassName={(date: Date): string => `date${formatDate(date, 'YYYYMMDD')}`}
      customInput={React.createElement(React.forwardRef(DateInput))}
    />
  );

  return (
    <StandardFormGroupWidget
      answerSource={answerSource}
      control={calendar}
      controlType="date-picker"
      dataType={dataType}
      error={error}
      helpInfo={data.helpInfo}
      helpInfoUI={ui.helpInfo}
      id={id}
      onClick={onChange}
      question={question}
      questionText={questionText}
      subtext={subtext}
      textAndStyle={textAndStyle}
    />
  );
}

interface HpCalendarIconProps {
  controlVariant: HpControlVariant;
  onClick: ((event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void) | undefined;
  question: HpClientQuestion;
}

function CalendarIcon({ controlVariant, onClick, question }: HpCalendarIconProps): JSX.Element {
  const { questionId } = question;
  const focusHandler = createCustomHandler({ action: 'slideDown', question });
  let widget: JSX.Element;
  switch (controlVariant) {
    case 'single-input':
      widget = <SvgIcon type={'calendar' as any} />;
      break;
    case 'multiple-inputs':
      widget = <ImageWidget image={calendarSrc} altText="Calendar" selected={false} />;
      break;
  }

  return (
    <button
      id={createControlId({ questionId, key: 'calendar' })}
      onFocus={focusHandler}
      onClick={onClick}
      type="button"
    >
      {widget}
    </button>
  );
}

interface HpGetDatePickerOptionsProps {
  validation: HpValidator<unknown>[];
  viewport: HpViewportState;
}

interface HpGetDatePickerOptionsResult {
  focusSelectedMonth: boolean | undefined;
  maxValue: Date | null;
  minValue: Date | null;
  monthsShown: number;
  showPreviousMonths: boolean;
  showSingleMonth: boolean;
}

function getDatePickerOptions({ validation, viewport }: HpGetDatePickerOptionsProps): HpGetDatePickerOptionsResult {
  const dateValidator = validation.filter((v) => v.getType() === 'date')[0] as HpDateValidator;
  const { minValue, maxValue } = getMinMaxDateValuesFromValidator(dateValidator);
  const { isDesktop } = viewport;
  const showSingleMonth = !isDesktop;
  let monthsShown = 1;
  if (!showSingleMonth) {
    if (minValue && maxValue) {
      const monthDifference = maxValue.getMonth() - minValue.getMonth() + 1;
      const yearDifference = maxValue.getFullYear() - minValue.getFullYear();
      monthsShown = monthDifference + 12 * yearDifference;
      if (monthsShown > 3) {
        monthsShown = 3;
      }
    } else {
      monthsShown = 3;
    }
  }

  // If max date is today or before, show past months
  let showPreviousMonths = false;
  let focusSelectedMonth: boolean | undefined = undefined;

  if (maxValue) {
    const today = new Date();
    showPreviousMonths = isSameDay(maxValue, today) || isEarlierDay(maxValue, today);
    focusSelectedMonth = showPreviousMonths || undefined;
  }

  return { minValue, maxValue, monthsShown, showSingleMonth, showPreviousMonths, focusSelectedMonth };
}

interface HpCreateCustomHandlerProps<HandlerInT = HpOnMouseEvent | HpOnFocusEvent> {
  action: 'slideUp' | 'slideDown';
  question: HpClientQuestion;
  handler?: HandlerInT | HpOnFocusEvent;
  preludeAction?: (e: BaseSyntheticEvent) => string;
}

function createCustomHandler<HandlerInT extends HpOnMouseEvent | HpOnFocusEvent>({
  action,
  question,
  handler,
  preludeAction
}: HpCreateCustomHandlerProps<HandlerInT>): (e: BaseSyntheticEvent) => void {
  const { questionId } = question;
  const customHandler: (e: BaseSyntheticEvent) => void = (e) => {
    if (preludeAction) {
      e.target.value = preludeAction(e);
      e.target.id = createControlId({ questionId });
    }

    if (handler) {
      handler(e as any, { type: 'standard', question });
    }

    toggleDatePickerPopup(e, action);
  };

  return customHandler;
}

function toggleDatePickerPopup(event: BaseSyntheticEvent, action: string): void {
  const datePicker = $(event.target).closest('.hp-date-picker').next().find('.react-datepicker');
  if (datePicker.length === 1) {
    if (action === 'slideUp') {
      $(datePicker).slideUp();
    } else {
      $(datePicker).slideDown();
    }
  }
}

interface HpCreateDateInputsProps {
  calendarIcon?: JSX.Element | null;
  disabled: boolean | undefined;
  name: string | undefined;
  onClick: ((event: HpMouseEvent) => void) | undefined;
  placeholder: string | undefined;
  questionId: string;
  questionText: string;
  type: string | undefined;
  value: Date | undefined;
}

function DateInputSingle({
  calendarIcon,
  disabled,
  name,
  onClick,
  placeholder,
  questionId,
  questionText,
  type,
  value
}: HpCreateDateInputsProps): JSX.Element {
  if (disabled) {
    onClick = undefined;
    calendarIcon = null;
  }

  const key = createControlId({ questionId });
  let control = (
    <FormControl
      key={key}
      id={key}
      placeholder={placeholder}
      name={name || questionId}
      className="hp-date-input-single"
      title={questionText}
      type={type || 'text'}
      readOnly
      disabled={disabled === true}
      onClick={onClick}
      value={value ? formatDate(value, 'DD MMM YYYY') : ''}
      tabIndex={-1}
    />
  );

  if (calendarIcon) {
    control = (
      <div className="hp-date-input-single-container">
        {control}
        {calendarIcon}
      </div>
    );
  }

  return control;
}

// Remove if we decide to use just one input. This only creates read-only inputs, onChange needs to be implemented and the dateParts recalculated if we wanted them to be editable.
function DateInputs({ onClick, placeholder, questionId, type, value }: HpCreateDateInputsProps): JSX.Element {
  let datePartValue: string | number | undefined = '';
  let datePartPlaceholder: string;
  const onClickHandler = onClick;
  const shouldCreatePlaceholder = placeholder !== '';
  const today = new Date();
  const dateParts: HpDatePart[] = ['day', 'month', 'year'];
  const elements = dateParts.map((datePart) => {
    switch (datePart) {
      case 'day':
        ({ datePartValue, datePartPlaceholder } = createDatePart('DD'));
        break;
      case 'month':
        ({ datePartValue, datePartPlaceholder } = createDatePart('MMMM'));
        break;
      case 'year':
        ({ datePartValue, datePartPlaceholder } = createDatePart('YYYY'));
        break;
      default:
        break;
    }

    const key = createControlId({ questionId, key: datePart });
    return (
      <FormControl
        key={key}
        id={key}
        placeholder={datePartPlaceholder}
        name={datePart}
        title={datePart}
        type={type || 'text'}
        readOnly
        onClick={createInputOnClickHandler(datePart, onClickHandler, onClick)}
        value={datePartValue}
        tabIndex={-1}
      />
    );

    function createDatePart(datePartFormat: string): {
      datePartValue: string | number | undefined;
      datePartPlaceholder: string;
    } {
      const datePartValue = value ? formatDate(value, datePartFormat) : undefined;
      const datePartPlaceholder = shouldCreatePlaceholder ? formatDate(today, datePartFormat) : '';
      return { datePartValue, datePartPlaceholder };
    }
  });

  return <>{elements}</>;
}

function createInputOnClickHandler(
  datePart: HpDatePart,
  onClickHandler: ((event: HpMouseEvent) => void) | undefined,
  onClick: ((event: HpMouseEvent) => void) | undefined
): ((event: HpMouseEvent) => void) | undefined {
  onClickHandler = (e: HpMouseEvent): void => {
    if (onClick) {
      onClick(e);
    }
  };

  return onClickHandler;
}

type HpMouseEvent = React.MouseEvent<HTMLInputElement, MouseEvent>;
